import React, { useState } from 'react';

import { EmailField, TextField } from '@crehana/ui';

import { Email } from '@crehana/ui/dist/EmailField/types';

import LazyPhoneInput from 'Jsx/Shared/PhoneInput';

interface ErrorState {
  message: string;
  error: boolean;
}

export type Type = 'email' | 'text' | 'document' | 'phone' | 'numeric';

export interface ChangeState {
  value: string;
  isValid: boolean;
}

export interface Validation {
  formattedValue: string;
  errorState: ErrorState;
}

type InputFieldProps = {
  label?: string | JSX.Element;
  description?: string;
  type?: Type;
  testId: string;
  id: string;
  name: string;
  maxLength?: number;
  startAdornment?: JSX.Element;
  autoFocus?: boolean;
  autoComplete?: string;
  onChange: ({ value, isValid }: ChangeState) => void;
  validation?: (event: React.ChangeEvent<HTMLInputElement>) => Validation;
  className?: string;
  hint?: string;
  controlledValue?: string;
};

const InputField = ({
  id,
  name,
  maxLength = 50,
  label,
  testId,
  type = 'text',
  autoFocus = false,
  autoComplete = '',
  description,
  startAdornment,
  validation,
  onChange,
  className = '',
  hint = '',
  controlledValue = '',
}: InputFieldProps) => {
  const [value, setValue] = useState<string>('');
  const [firstTry, setFirstTry] = useState<boolean>(true);
  const [error, setError] = useState<ErrorState>({
    message: 'EMPTY',
    error: true,
  });

  const handleBlur = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value: currentValue } = event.target;

    let errorState: ErrorState = { error: false, message: '' };
    let formattedValue: string = value;

    if (validation) {
      const validationResult = validation(event);

      formattedValue = validationResult.formattedValue;
      errorState = validationResult.errorState;
    }

    setFirstTry(false);
    setValue(formattedValue);
    setError(errorState);
    onChange({ value: currentValue, isValid: !errorState.error });
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value: currentValue } = event.target;
    let errorState: ErrorState = error;
    let formattedValue: string = currentValue;

    if (validation) {
      const validationResult = validation(event);

      formattedValue = validationResult.formattedValue;
      errorState = validationResult.errorState;
    }

    setValue(formattedValue);
    setError(errorState);
    onChange({ value: currentValue, isValid: !errorState.error });
  };

  const handleEmailChange = ({
    value: currentEmail,
    error: { message },
  }: Email) => {
    setValue(currentEmail);
    setError({ message, error: message !== '' });
    onChange({
      value: currentEmail,
      isValid: message === '' && currentEmail.length !== 0,
    });
  };

  const handlePhoneChange = ({
    fullNumber,
    error: message,
    isValid,
  }: {
    fullNumber: string;
    error: string;
    isValid: boolean;
  }) => {
    setFirstTry(false);
    setValue(fullNumber);
    setError({ message, error: !isValid });
    onChange({ value: fullNumber, isValid });
  };

  return (
    <>
      {type === 'email' && (
        <EmailField
          id={id}
          name={name}
          data-testid={testId}
          autoComplete={autoComplete}
          className={className}
          hint={hint}
          full
          onEmailChange={handleEmailChange}
          label={label}
          autoFocus={autoFocus}
          errorText={firstTry ? '' : error.message}
        />
      )}
      {(type === 'document' || type === 'text' || type === 'numeric') && (
        <TextField
          id={id}
          autoComplete={autoComplete}
          name={name}
          inputProps={type === 'numeric' ? { inputMode: 'numeric' } : {}}
          data-testid={testId}
          className={className}
          type={type}
          label={label}
          hint={hint}
          maxLength={maxLength}
          full
          autoFocus={autoFocus}
          onBlur={handleBlur}
          onChange={handleChange}
          error={firstTry ? false : error.error}
          errorText={firstTry ? '' : error.message}
          check={firstTry ? false : !error.error}
          value={controlledValue || value}
          description={description}
          startAdornment={startAdornment}
        />
      )}
      {type === 'phone' && (
        <LazyPhoneInput
          className={className}
          autoFocus={autoFocus}
          label={typeof label === 'string' ? label : undefined}
          check={firstTry ? false : !error.error}
          onChange={handlePhoneChange}
        />
      )}
    </>
  );
};

export default InputField;
